/**
 * Stores the default state of the `searchTags` property from the `arkivMetaStore`.
 * 
 * This object is used in the `setKortSearch` and `setDokumentSearch` functions
 * within `arkivMetaStore.js` to set or reset the state to its default values.
 * 
 * Parameter value types:
 * - **Set**: An empty set indicates no filters. Otherwise, contains string values.
 * - **String**: An empty string (`''`) indicates no filter. Otherwise, contains a string value.
 * - **Number**: `Number.NaN` indicates no filter. Otherwise, contains a number value.
 * 
 * @constant
 * @type {Object}
 * @property {Object} global - Global search parameters.
 * @property {Object} kort - Map-specific search parameters.
 * @property {Object} dokument - Document-specific search parameters.
 */

export const searchTagSetup = {
  global: {
    fritekstsoegning: '',
    geometri: '',
    titel: ''
  },
  kort: {
    daekningsomraade: new Set(),
    kortgruppe: new Set(),
    kortvaerk: new Set(),
    maalestok: new Set(),
    kortbladnummer: '',
    tegner: '',
    gaeldendeperiode_gaeldendefra: Number.NaN,
    gaeldendeperiode_gaeldendetil: Number.NaN,
  },
  dokument: {
    dokumentsamling: new Set(),
    herredsnavn: '',
    sognenavn: '',
    herredsnummer: Number.NaN,
    sogneid: Number.NaN
  }
}

/**
 * Stores the default state of the `displayTags` property from the `arkivMetaStore`.
 * 
 * This object is used in the `setKortSearch` and `setDokumentSearch` functions
 * within `arkivMetaStore.js` to set or reset the state to its default values.
 * 
 * @constant
 * @type {Object}
 * @property {number} limit - The number of search results to fetch.
 * @property {number} offset - The offset (starting from 0) from which to begin fetching results.
 * @property {string} sort - The metadata property to sort by.  
 *   Valid values for **KortSearch**: `kortgruppe`, `gaeldendeperiode_gaeldendefra`, `gaeldendeperiode_gaeldendetil`, `kortvaerk`, `maalestok`, `titel`.  
 *   Valid values for **DokumentSearch**: `dokumentsamling`, `herredsnavn`, `herredsnummer`, `sogneid`, `sognenavn`, `titel`.
 * @property {string} direction - The sort direction. Valid values are `'asc'` and `'desc'`.
 */

export const displayTagSetupMap = {
  limit: 50,
  offset: 0,
  sort: 'maalestok',
  direction: 'asc'
}

export const displayTagSetupDocument = {
  limit: 50,
  offset: 0,
  sort: 'titel',
  direction: 'asc'
}

/**
 * Stores the initial value of the `displayMetaData` property from `arkivMetaStore`.
 * 
 * During the `fetchDisplayMetaData` process, the base URL (from `.env.[environment]`) is combined with each group's `.endpoint` 
 * to fetch the corresponding CMS data. The result is stored in `.value`. While the fetch is in progress, `.isLoading` is set to `true`. 
 * If the fetch fails, the error message is stored in `.error`.
 * 
 * @constant
 * @type {Object<string, {endpoint: string, value: *, isLoading: boolean, error: *}>}
 */
export const displayDataSetup = {
  kortgrupper: {
    endpoint:  'metadata/kortgrupper/kortvaerker',
    value: null,
    isLoading: false,
    error: null  
  },
  maalestok: {
    endpoint: 'metadata/maalestok',
    value: null,
    isLoading: false,
    error: null 
  },
  sognenavn: {
    endpoint:  'metadata/sognenavn',
    value: null,
    isLoading: false,
    error: null 
  },
  herredsnavn: {
    endpoint:  'metadata/herredsnavn',
    value: null,
    isLoading: false,
    error: null  
  },
  daekningsomraader: {
    endpoint:  'metadata/daekningsomraader',
    value: null,
    isLoading: false,
    error: null 
  }
}


/**
 * Contains a mapping from a type to the keys in `searchTags` from `arkivMetaStore` that contain that type. 
 * It is used to provide an enum-like approach to handling input to actions in the store. 
 * 
 * @constant
 * @type {Object}
 * @property {Set<string>} setKeys - Keys containing a set of strings
 * @property {Set<string>} stringKeys -Keys containing a single string
 * @property {Set<string>} numberKeys - Keys containing a single number
 */
export const searchKeyTypes = {
  setKeys : new Set(['kortgruppe', 'dokumentsamling', 'kortvaerk', 'maalestok', 'daekningsomraade']),
  stringKeys : new Set(['fritekstsoegning', 'titel', 'kortbladnummer', 'tegner', 'sognenavn', 'geometri', 'herredsnavn']),
  numberKeys : new Set(['gaeldendeperiode_gaeldendefra', 'gaeldendeperiode_gaeldendetil', 'herredsnummer', 'sogneid']),
}


/**
 * Contains a mapping from a type to the keys in `displayTags` from `arkivMetaStore` that contain that type. 
 * It is used to provide an enum-like approach to handling input to actions in the store. 
 * 
 * @constant
 * @type {Object}
 * @property {Set<string>} numberKeys - Keys containing a single number
 * @property {Set<string>} stringKeys -Keys containing a single string
 */
const displayKeyTypes = {
  numberKeys : new Set(['limit', 'offset']),
  stringKeys : new Set(['sort', 'direction'])
}

/**
 * Checks if a value is a string.
 * @param {*} tag - The value to check.
 * @returns {boolean}
 */
export const isString = (tag) => { return typeof tag === 'string' || tag instanceof String}
/**
 * Checks if a value is a valid number (not NaN).
 * @param {*} tag - The value to check.
 * @returns {boolean}
 */
export const isNumber = tag => { return typeof tag === 'number' && !isNaN(tag) }


/**
 * Receives a key–value pair and determines whether the key is a known search key
 * and if the value (`tag`) has a valid type for that key. Used for validating
 * entries before inserting them into the `searchTags` property of the `arkivMetaStore`.
 * 
 * @param {string} key - The key to check.
 * @param {*} tag - The corresponding value to validate.
 * @returns {'set' | 'string' | 'number' | 'unknown'} - The type classification of the key–tag pair, or `'unknown'` if invalid.
 */

export const getSearchPairType = (key, tag) => {
  if (searchKeyTypes.setKeys.has(key) && isString(tag)) return 'set'
  if (searchKeyTypes.stringKeys.has(key) && isString(tag)) return 'string'
  if (searchKeyTypes.numberKeys.has(key) && isNumber(tag)) return 'number'
  return 'unknown' 
}

/**
 * Receives a key and determines whether the key is a known search key. Used for validating
 * entries before accessing the `searchTags` property in getter methods from `arkivMetaStore`.
 * 
 * @param {string} key - The key to check.
 * @returns {'set' | 'string' | 'number' | 'unknown'} - The type classification of the key, or `'unknown'` if invalid.
 */
export const getSearchKeyType = (key) => {
  if (searchKeyTypes.setKeys.has(key)) return 'set'
  if (searchKeyTypes.stringKeys.has(key)) return 'string'
  if (searchKeyTypes.numberKeys.has(key)) return 'number'
  return 'unknown' 
}

/**
 * Receives a key–value pair and determines whether the key is a known display key
 * and if the value (`tag`) has a valid type for that key. Used for validating
 * entries before inserting them into the `displayTags` property of the `arkivMetaStore`.
 * 
 * @param {string} key - The key to check.
 * @param {*} tag - The corresponding value to validate.
 * @returns {'string' | 'number' | 'unknown'} - The type classification of the key–tag pair, or `'unknown'` if invalid.
 */

export const validateDisplayKey = (key, tag) => {
  if(displayKeyTypes.numberKeys.has(key) && isNumber(tag)) { return true }
  if(displayKeyTypes.stringKeys.has(key) && isString(tag)) { return true }
  return false
}

/**
 * Receives a key and determines whether the key is a known search key. Used for validating
 * entries before accessing the `displayTags` property in getter methods from `arkivMetaStore`.
 * 
 * @param {string} key - The key to check.
 * @returns {'set' | 'string' | 'number' | 'unknown'} - The type classification of the key, or `'unknown'` if invalid.
 */
export const getDisplayKeyType = (key) => {
  if(displayKeyTypes.numberKeys.has(key)) { return 'number'}
  if(displayKeyTypes.stringKeys.has(key)) { return 'string' }
  return 'unknown'
}


/**
 * Determines the origin (`searchTags` or `displayTags`) and data type of a given key 
 * used in the `arkivMetaStore`, by delegating to `getSearchKeyType` and `getDisplayKeyType`.
 * 
 * This is primarily used when processing generic keys—such as those parsed from URL query parameters—
 * to identify how they should be handled.
 * 
 * @param {string} key - The key to classify.
 * @returns {[ 'searchParam' | 'displayParam' | 'unknown', 'set' | 'string' | 'number' | 'unknown' ]}
 * A tuple where:
 * - The first element identifies the source (`'searchParam'`, `'displayParam'`, or `'unknown'`).
 * - The second element specifies the expected type of the key (`'set'`, `'string'`, `'number'`, or `'unknown'`).
 */
export const getKeyType = (key) => {
  const searchType = getSearchKeyType(key)
  const displayType = getDisplayKeyType(key)

  if(searchType === 'unknown' && displayType === 'unknown') {
    return ['unknown', 'unknown']
  }

  if(searchType !== 'unknown') {
    return ['searchParam', searchType ]
  }

  if(displayType !== 'unknown') {
    return ['displayParam', displayType]
  }

}
